import React, { useState } from "react";
import { Badge as ShadBadge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  DebitPermissionDto,
  GetRechnungDetailsDto,
  PatientBillInfoDto,
  TherapyInfoDto,
} from "../../../../api-client";
import PatientBillActions from "../components/PatientBillActions";
import { formatDateUI } from "../../../../helpers/dateFormat";
import PatientBillDetailsModalShad from "../dialogs/PatientBillDetailsModalShad";

interface RxPriceTableRowProps {
  bill: PatientBillInfoDto;
  index: number;
  rechnungMap: Record<string, GetRechnungDetailsDto[]>;
  debitPermissions: DebitPermissionDto[];
  sepaMenuState: {
    anchorEl: HTMLElement | null;
    openMenus: { [key: string]: boolean };
  };
  isRowBusy: (rxId?: string) => boolean;
  onClickPay: (bill: PatientBillInfoDto) => void;
  onClickBill: (bill: PatientBillInfoDto) => void;
  onClickPaybackToPatient: (bill: PatientBillInfoDto) => void;
  onResumeEinmal: (bill: PatientBillInfoDto) => void;
  onHandleSepaButtonClick: (
    bill: PatientBillInfoDto,
    validSepaPermissions: DebitPermissionDto[],
  ) => void;
  onHandleSepaSelect: (bill: PatientBillInfoDto, permission: DebitPermissionDto) => void;
  onCloseSepaMenu: (rxId?: string) => void;
  handleExternalBill: (bill: PatientBillInfoDto) => void;
}

/**
 * shadcn row for the redesigned "Rezepte" bill table (V2). Identical logic/data to
 * the original `PatientBillRow` (opens `PatientBillDetailsModalShad`, reuses
 * `PatientBillActions`); only the presentation matches the SEPA card design.
 */
const RxPriceTableRow: React.FC<RxPriceTableRowProps> = ({
  bill,
  index,
  rechnungMap,
  debitPermissions,
  sepaMenuState,
  isRowBusy,
  onClickPay,
  onClickBill,
  onClickPaybackToPatient,
  onResumeEinmal,
  onHandleSepaButtonClick,
  onHandleSepaSelect,
  onCloseSepaMenu,
  handleExternalBill,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleRowClick = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const rxId = bill.rxId;
  const rechnungen = rxId ? rechnungMap[rxId] : undefined;
  const rxPrice = bill.isEstimated && bill.isTimeSlotZero ? bill.estimatedPrice : bill.realPrice;

  /**
   * Resolve which SEPA debit mandates can be used for this bill's due amount.
   * - Single-use: valid only when its fixed amount matches the Fällig (dueAmount).
   * - Multi-use:  always valid (can charge any amount).
   * - If both exist: prefer the matching single-use mandate, otherwise fall back
   *   to the multi-use mandate(s).
   */
  const due = Number(bill.dueAmount).toFixed(2);

  const permissions = Array.isArray(debitPermissions) ? debitPermissions : [];

  const matchingSingleUse = permissions.filter(
    (permission) =>
      permission.isSingleUse &&
      permission.amount != null &&
      Number(permission.amount).toFixed(2) === due,
  );
  const multiUsePermissions = permissions.filter((permission) => !permission.isSingleUse);

  const validSepaPermissions =
    matchingSingleUse.length > 0 ? matchingSingleUse : multiUsePermissions;

  const zebra = index % 2 === 1 ? "bg-slate-100" : "bg-white";
  // Which number to show stays tied to the backend's own price selection (see
  // rxPrice above) so the displayed amount and dueAmount cannot drift apart.
  // Whether to MARK it as an estimate is a different question, and the backend
  // answers it: a price is provisional until every prescribed unit is scheduled.
  const showsEstimatedPrice = bill.isEstimated && bill.isTimeSlotZero;
  const isProvisional = bill.isProvisionalPrice;

  return (
    <>
      <TableRow
        onClick={handleRowClick}
        className={`cursor-pointer text-base ${zebra} hover:bg-slate-100/70`}
      >
        <TableCell className="py-1 text-left font-medium text-slate-800">{bill.rxNumber}</TableCell>
        <TableCell className="whitespace-nowrap py-1 text-left text-slate-600">
          {formatDateUI(bill.date)}
        </TableCell>
        <TableCell className="py-1 text-left text-slate-700">
          {bill.therapies
            ?.map((therapyInfo: TherapyInfoDto) => `${therapyInfo.therapy} - ${therapyInfo.amount}`)
            .join(", ")}
        </TableCell>
        <TableCell className="whitespace-nowrap py-1 text-right font-medium text-slate-800">
          <span className="flex items-center justify-between gap-2">
            <span>
              {isProvisional ? (
                <ShadBadge
                  variant="outline"
                  className="gap-1 border-red-200 bg-red-100 px-2 py-0 text-sm font-normal text-red-800"
                >
                  geschätzter Preis
                </ShadBadge>
              ) : null}
            </span>
            <span>
              {Number(showsEstimatedPrice ? bill.estimatedPrice : bill.realPrice)
                .toFixed(2)
                .replace(".", ",")}
              {" € "}
            </span>
          </span>
        </TableCell>
        <TableCell className="whitespace-nowrap py-1 text-right text-slate-700">
          {Number(bill.amountPaid).toFixed(2).replace(".", ",") + " €"}
        </TableCell>
        <TableCell className="whitespace-nowrap py-1 text-right text-slate-700">
          {Number(bill.dueAmount).toFixed(2).replace(".", ",") + " €"}
        </TableCell>
        <TableCell className="py-1 text-center text-slate-700">
          {bill.insuranceTransactionNumber}
        </TableCell>
        <TableCell className="py-1 text-center" onClick={(e) => e.stopPropagation()}>
          <PatientBillActions
            bill={bill}
            rechnungen={rechnungen}
            rxPrice={rxPrice}
            validSepaPermissions={validSepaPermissions}
            sepaMenuState={sepaMenuState}
            isRowBusy={isRowBusy}
            onClickPay={onClickPay}
            onClickBill={onClickBill}
            onClickPaybackToPatient={onClickPaybackToPatient}
            onResumeEinmal={onResumeEinmal}
            onHandleSepaButtonClick={onHandleSepaButtonClick}
            onHandleSepaSelect={onHandleSepaSelect}
            onCloseSepaMenu={onCloseSepaMenu}
            handleExternalBill={handleExternalBill}
          />
        </TableCell>
      </TableRow>

      <PatientBillDetailsModalShad
        open={isModalOpen}
        onClose={handleCloseModal}
        metaDataBill={bill}
      />
    </>
  );
};

export default RxPriceTableRow;
